import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { AddSuretyLead } from "@/components/AddSuretyLead";
import { SuretyLeadImport } from "@/components/SuretyLeadImport";

const Pricing = ({ p, reload }) => {
  const [v, setV] = useState(p);
  const save = async () => {
    try { await api.put("/admin/surety/pricing", { run_price: Number(v.run_price), postcard_price: Number(v.postcard_price), max_leads: Number(v.max_leads) }); toast.success("Surety pricing saved"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="mb-5 p-6" data-testid="surety-pricing">
      <h3 className="mb-3 font-display text-lg font-bold text-slate-900">Lead run pricing</h3>
      <div className="flex flex-wrap items-end gap-4">
        {[["run_price", "Price per lead run ($)"], ["postcard_price", "Price per postcard ($)"], ["max_leads", "Max leads per run"]].map(([k, l]) => (
          <div key={k}><Label className="text-xs">{l}</Label><Input type="number" min="0" value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="w-40" data-testid={`surety-${k}`} /></div>
        ))}
        <Button onClick={save} data-testid="surety-pricing-save">Save pricing</Button>
      </div>
    </Card>
  );
};

const AutopilotSwitches = ({ a, reload }) => {
  const set = async (k, v) => {
    try { await api.put("/admin/surety/autopilot", { [k]: v }); toast.success("Saved"); reload(); window.dispatchEvent(new Event("dnc-bypass-changed")); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="mb-5 p-6" data-testid="surety-autopilot-admin">
      <h3 className="mb-3 font-display text-lg font-bold text-slate-900">AI Autopilot controls</h3>
      <div className="flex flex-col gap-3">
        <label className="flex items-center gap-3 text-sm"><Switch checked={a.dialing} onCheckedChange={(v) => set("dialing", v)} data-testid="surety-dialing-toggle" /><span><b>AI dialing</b> - when off, Autopilot only runs leads, emails and texts (no calls)</span></label>
        <label className="flex items-center gap-3 text-sm"><Switch checked={a.dnc_bypass} onCheckedChange={(v) => set("dnc_bypass", v)} data-testid="surety-dnc-bypass-toggle" /><span><b>Bypass Do-Not-Call scrubbing</b> - {a.dnc_bypass ? <span className="font-semibold text-red-600">ON: calls and texts are NOT checked against the DNC lists</span> : "off: every number is checked with RealValidation first"}</span></label>
      </div>
    </Card>
  );
};

const Agent = ({ a, reload }) => {
  const set = async (status) => {
    try { await api.post(`/admin/surety/agents/${a.id}/status`, { status }); toast.success(status === "active" ? "Agent approved" : "Agent suspended"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex flex-col gap-2 border-t border-slate-100 py-3 text-sm md:flex-row md:items-center" data-testid={`surety-agent-${a.id}`}>
      <div className="md:w-1/3"><div className="font-semibold text-slate-900">{a.first_name} {a.last_name}</div><div className="text-xs text-slate-500">{a.email} · NPN {a.npn || "—"} · {a.carrier || "no carrier"}</div></div>
      <div className="text-xs text-slate-600 md:w-1/4">{a.runs} paid runs · {a.leads} leads</div>
      <div className="flex items-center gap-2 md:flex-1 md:justify-end">
        <Badge variant="outline" className="capitalize">{a.account_status}</Badge>
        {a.account_status !== "active" && <Button size="sm" onClick={() => set("active")} data-testid={`surety-approve-${a.id}`}>Approve</Button>}
        {a.account_status === "active" && <Button size="sm" variant="outline" onClick={() => set("suspended")} data-testid={`surety-suspend-${a.id}`}>Suspend</Button>}
      </div>
    </div>
  );
};

export default function AdminSurety() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/surety").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  return (
    <div data-testid="admin-surety-page">
      <PageHeader title="Surety Bonds" subtitle={`Surety agent portal - kept separate from the mortgage side. DBPR contractors loaded: ${d.dbpr_count.toLocaleString()}${d.dbpr_loaded_at ? ` (${new Date(d.dbpr_loaded_at).toLocaleDateString()})` : " (loads on the first run)"}`} />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-surety-add-leads">
        <AddSuretyLead agents={d.agents.filter((a) => a.status === "active")} />
        <SuretyLeadImport agents={d.agents.filter((a) => a.status === "active")} />
      </div>
      <Pricing p={d.pricing} reload={load} />
      <AutopilotSwitches a={d.autopilot} reload={load} />
      <Card className="mb-5 p-6"><h3 className="mb-1 font-display text-lg font-bold text-slate-900">Surety agents</h3>
        <p className="mb-2 text-sm text-slate-600">Agents sign up on the login page by choosing “Surety bond agent”. Approve them here before they can sign in.</p>
        {!d.agents.length && <p className="text-sm text-slate-500">No surety agents yet.</p>}
        {d.agents.map((a) => <Agent key={a.id} a={a} reload={load} />)}
      </Card>
      <Card className="p-6"><h3 className="mb-2 font-display text-lg font-bold text-slate-900">Recent lead runs</h3>
        {!d.runs.length && <p className="text-sm text-slate-500">No runs yet.</p>}
        {d.runs.map((r) => (
          <div key={r.id} className="flex flex-wrap justify-between gap-2 border-t border-slate-100 py-2 text-sm" data-testid={`admin-surety-run-${r.id}`}>
            <span className="font-semibold">{r.agent_name} · {r.lead_count} leads · {r.license_types.join(", ")}</span>
            <span className="text-xs text-slate-600">{r.status.replace("_", " ")} · {r.found} found · {r.emailed} emailed · {r.mailed} postcards · ${Number(r.price).toFixed(2)}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}
